import type { AxiosRequestConfig } from 'axios'
import { type ApiError, type FieldErrors } from './http'
import { extractErrorMessage, http } from './https'
import { showToast } from './toast'

export type ApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false
      error: ApiError
      message: string
      status?: number
      fieldErrors?: FieldErrors | null
    }

export async function api<T>(config: AxiosRequestConfig): Promise<ApiResult<T>> {
  try {
    const res = await http.request<T>(config)
    return { ok: true, data: res.data }
  } catch (e) {
    const err = e as ApiError
    const status = err.response?.status
    const message =
      (err.response?.data as any)?.message ||
      (err.response?.data as any)?.error ||
      err.message ||
      'Request failed'

    return {
      ok: false,
      error: err,
      status,
      message,
      fieldErrors: err.fieldErrors,
    }
  }
}

export function unwrapApiResult<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data

  const err = result.error
  ;(err as any).status = result.status
  ;(err as any).fieldErrors = result.fieldErrors
  ;(err as any).friendlyMessage = result.message

  throw err
}

type RetryOptions = {
  retries: number
  baseDelayMs?: number
  maxDelayMs?: number
}

function isRetryableNetworkError(err: ApiError): boolean {
  if (err.response) return false

  const code = (err as any).code as string | undefined
  const msg = String(err.message || '').toLowerCase()

  if (code === 'ECONNABORTED') return true
  if (code === 'ERR_NETWORK') return true
  if (msg.includes('network error')) return true
  if (msg.includes('timeout')) return true

  return true
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

function backoffMs(attemptIndex: number, baseDelayMs: number, maxDelayMs: number): number {
  const exp = baseDelayMs * Math.pow(2, attemptIndex)
  const capped = Math.min(exp, maxDelayMs)
  const jitter = 0.8 + Math.random() * 0.4
  return Math.round(capped * jitter)
}

export async function apiWithRetry<T>(
  config: AxiosRequestConfig,
  options: RetryOptions
): Promise<ApiResult<T>> {
  const retries = Math.max(0, options.retries)
  const baseDelayMs = options.baseDelayMs ?? 300
  const maxDelayMs = options.maxDelayMs ?? 2000

  for (let attempt = 0; attempt <= retries; attempt++) {
    const isLastAttempt = attempt === retries

    const configWithMeta: AxiosRequestConfig & { meta?: { suppressToast?: boolean } } = {
      ...config,
      meta: {
        ...(config as any).meta,
        suppressToast: true,
      },
    }

    const result = await api<T>(configWithMeta)

    if (result.ok) return result

    const retryable = isRetryableNetworkError(result.error)

    if (!retryable || isLastAttempt) {
      showToast({ message: extractErrorMessage(result.message), variant: 'danger' })
      return result
    }

    await sleep(backoffMs(attempt, baseDelayMs, maxDelayMs))
  }

  return api<T>(config)
}

export interface PaginationMeta {
  currentPage: number
  from: number | null
  lastPage: number
  perPage: number
  to: number | null
  total: number
}

export interface PaginatedResult<T> {
  items: T[]
  pagination: PaginationMeta
}

export interface LaravelPaginationMetaResponse {
  current_page?: unknown
  from?: unknown
  last_page?: unknown
  path?: unknown
  per_page?: unknown
  to?: unknown
  total?: unknown
  links?: unknown
}

export interface PaginatedApiResponse<T> {
  data?: T[]
  meta?: LaravelPaginationMetaResponse
}

function toNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function toNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}
export function parsePaginationMeta(meta: unknown): PaginationMeta {
  const item = typeof meta === 'object' && meta !== null ? meta : {}

  return {
    currentPage: toNumber((item as any).current_page, 1),
    from: toNullableNumber((item as any).from),
    lastPage: toNumber((item as any).last_page, 1),
    perPage: toNumber((item as any).per_page, 0),
    to: toNullableNumber((item as any).to),
    total: toNumber((item as any).total, 0),
  }
}

export function parsePaginatedResult<TInput, TOutput>(
  response: PaginatedApiResponse<TInput>,
  mapper: (item: TInput) => TOutput
): PaginatedResult<TOutput> {
  return {
    items: Array.isArray(response?.data) ? response.data.map(mapper) : [],
    pagination: parsePaginationMeta(response?.meta),
  }
}
